import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useBranding } from "@/contexts/BrandingContext";
import { usePresentationMode } from "@/contexts/PresentationModeContext";
import { useChartMask } from "@/components/ChartMask";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Users,
  UserCheck,
  Target,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Building2,
  BarChart3,
  RefreshCw,
  Globe,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";
import PageDateFilter from "@/components/PageDateFilter";
import PageNote from "@/components/PageNote";

const DashboardPage = () => {
  const { sessionToken, user } = useAuth();
  const { chartColors, pageTitles } = useBranding();
  const { isPresentationMode, maskNumber } = usePresentationMode();
  const { maskAxisTick, maskTooltip, maskCurrencyTooltip, showLabels } = useChartMask();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const [dateFilterInitialized, setDateFilterInitialized] = useState(false);

  // CRITICAL: Track tenant ID in state to trigger re-fetches when tenant changes
  const [currentTenantId, setCurrentTenantId] = useState(() => sessionStorage.getItem("viewing_tenant_id"));
  const [trafficData, setTrafficData] = useState([
    { name: "Mon", sessions: 0, users: 0, priorSessions: 0, priorUsers: 0 },
    { name: "Tue", sessions: 0, users: 0, priorSessions: 0, priorUsers: 0 },
    { name: "Wed", sessions: 0, users: 0, priorSessions: 0, priorUsers: 0 },
    { name: "Thu", sessions: 0, users: 0, priorSessions: 0, priorUsers: 0 },
    { name: "Fri", sessions: 0, users: 0, priorSessions: 0, priorUsers: 0 },
    { name: "Sat", sessions: 0, users: 0, priorSessions: 0, priorUsers: 0 },
    { name: "Sun", sessions: 0, users: 0, priorSessions: 0, priorUsers: 0 },
  ]);
  
  // Listen for tenant changes
  useEffect(() => {
    const checkTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId !== currentTenantId) {
        console.log("=== DashboardPage: TENANT CHANGED ===", currentTenantId, "->", newTenantId);
        setCurrentTenantId(newTenantId);
      }
    };
    const interval = setInterval(checkTenantChange, 500);
    return () => clearInterval(interval);
  }, [currentTenantId]);

  const handleDateChange = useCallback((dateInfo) => {
    console.log("Dashboard date filter changed:", dateInfo);
    setDateFilter(dateInfo);
    setDateFilterInitialized(true);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      if (dateFilter?.startDate) params.start_date = dateFilter.startDate;
      if (dateFilter?.endDate) params.end_date = dateFilter.endDate;
      
      console.log("=== DashboardPage fetchDashboardData ===", "tenantId:", viewingTenantId);
      const response = await axios.get(`${API}/analytics/dashboard`, { params });
      console.log("Dashboard response:", response.data);
      setDashboardData(response.data);
      
      // Use daily_activity from dashboard as fallback for traffic chart
      const dailyActivity = response.data?.daily_activity || [];
      const priorActivity = response.data?.prior_period_activity || [];
      if (dailyActivity.length > 0) {
        const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        
        // Build a map of prior period data by day name
        const priorByDay = {};
        priorActivity.forEach(d => {
          const date = new Date(d.date);
          const dayName = dayNames[date.getDay()];
          priorByDay[dayName] = { contacts: d.contacts || 0, mqls: d.mqls || 0 };
        });
        
        const chartData = dailyActivity.slice(-7).map(d => {
          const date = new Date(d.date);
          const dayName = dayNames[date.getDay()];
          const prior = priorByDay[dayName] || { contacts: 0, mqls: 0 };
          return {
            name: dayName,
            sessions: d.contacts || 0,  // Use contacts as "sessions" proxy
            users: d.mqls || 0,          // Use MQLs as "users" proxy
            priorSessions: prior.contacts,
            priorUsers: prior.mqls
          };
        });
        console.log("Dashboard using daily_activity for chart:", chartData);
        setTrafficData(chartData);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    }
  };

  const fetchTrafficData = async () => {
    // NOTE: This function is disabled for now because we want to use the daily_activity 
    // from the dashboard endpoint which includes prior period comparison data.
    // HubSpot traffic sources doesn't include prior period data for comparison charts.
    console.log("Using dashboard daily_activity for traffic chart (includes prior period comparison)");
  };

  const seedDemoData = async () => {
    setRefreshing(true);
    try {
      await axios.post(
        `${API}/seed`,
        {},
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Demo data loaded successfully!");
      await fetchDashboardData();
    } catch (error) {
      console.error("Error seeding data:", error);
      toast.error("Failed to load demo data");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Only fetch data after date filter is initialized
    if (!dateFilterInitialized) return;
    
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboardData(),
        fetchTrafficData()
      ]);
      setLoading(false);
    };
    loadData();
  }, [sessionToken, dateFilterInitialized, dateFilter?.startDate, dateFilter?.endDate, currentTenantId]);

  // Show loading with header while waiting for date filter or data
  if (loading || !dateFilterInitialized) {
    return (
      <div className="animate-fade-in" data-testid="dashboard-page">
        {/* Page Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">{pageTitles?.dashboard || "Dashboard"}</h1>
            <p className="text-sm text-slate-500 mt-1">
              Welcome back, {user?.name}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <PageDateFilter pageId="dashboard" onDateChange={handleDateChange} />
          </div>
        </div>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="w-10 h-10 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const summary = dashboardData?.summary || {};
  const dealStages = dashboardData?.deal_stages || [];

  // Transform deal stages for chart - mask values in presentation mode
  const dealStageData = dealStages.map((stage) => ({
    name: stage._id?.replace(/_/g, " ") || "Unknown",
    count: isPresentationMode ? Math.round(Math.random() * 10 + 5) : stage.count,
    value: isPresentationMode ? 0 : stage.value,
  }));

  // Transform traffic data for chart - use dashboard traffic if available, otherwise show placeholder
  const displayTrafficData = isPresentationMode 
    ? [
        { name: "Mon", sessions: "***", users: "***" },
        { name: "Tue", sessions: "***", users: "***" },
        { name: "Wed", sessions: "***", users: "***" },
        { name: "Thu", sessions: "***", users: "***" },
        { name: "Fri", sessions: "***", users: "***" },
        { name: "Sat", sessions: "***", users: "***" },
        { name: "Sun", sessions: "***", users: "***" },
      ]
    : trafficData.length > 0 && trafficData.some(d => d.sessions > 0)
      ? trafficData 
      : [
          { name: "Mon", sessions: dashboardData?.traffic?.mon || 0, users: 0 },
          { name: "Tue", sessions: dashboardData?.traffic?.tue || 0, users: 0 },
          { name: "Wed", sessions: dashboardData?.traffic?.wed || 0, users: 0 },
          { name: "Thu", sessions: dashboardData?.traffic?.thu || 0, users: 0 },
          { name: "Fri", sessions: dashboardData?.traffic?.fri || 0, users: 0 },
          { name: "Sat", sessions: dashboardData?.traffic?.sat || 0, users: 0 },
          { name: "Sun", sessions: dashboardData?.traffic?.sun || 0, users: 0 },
        ];

  // Lifecycle distribution for pie chart - mask in presentation mode
  const lifecycleData = isPresentationMode ? [
    { name: "Leads", value: 100 },
    { name: "MQLs", value: 45 },
    { name: "SQLs", value: 18 },
  ] : [
    { name: "Leads", value: summary.total_contacts - summary.mqls - summary.sqls || 100 },
    { name: "MQLs", value: summary.mqls || 45 },
    { name: "SQLs", value: summary.sqls || 18 },
  ].filter((item) => item.value > 0);

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            Welcome back, {user?.name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PageDateFilter pageId="dashboard" onDateChange={handleDateChange} />
        </div>
      </div>

      {/* Page Notes & Market Insights */}
      <PageNote pageId="dashboard" />

      {/* KPI Cards */}
      <div className="kpi-grid">
        <Card className="kpi-card" data-testid="kpi-contacts">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Total Contacts</p>
                <p className="kpi-value">{maskNumber(summary.total_contacts || 0)}</p>
                <p className={`kpi-change ${summary.contacts_change >= 0 ? 'positive' : 'negative'}`}>
                  {summary.contacts_change >= 0 ? (
                    <TrendingUp className="w-3 h-3 inline mr-1" />
                  ) : (
                    <TrendingDown className="w-3 h-3 inline mr-1" />
                  )}
                  {isPresentationMode ? '~**%' : `${summary.contacts_change >= 0 ? '+' : ''}${summary.contacts_change || 0}%`} vs prior period
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card" data-testid="kpi-mqls">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">MQLs</p>
                <p className="kpi-value">{maskNumber(summary.mqls || 0)}</p>
                <p className={`kpi-change ${summary.mqls_change >= 0 ? 'positive' : 'negative'}`}>
                  {summary.mqls_change >= 0 ? (
                    <TrendingUp className="w-3 h-3 inline mr-1" />
                  ) : (
                    <TrendingDown className="w-3 h-3 inline mr-1" />
                  )}
                  {isPresentationMode ? '~**%' : `${summary.mqls_change >= 0 ? '+' : ''}${summary.mqls_change || 0}%`} vs prior period
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card" data-testid="kpi-sqls">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">SQLs</p>
                <p className="kpi-value">{maskNumber(summary.sqls || 0)}</p>
                <p className={`kpi-change ${summary.sqls_change >= 0 ? 'positive' : 'negative'}`}>
                  {summary.sqls_change >= 0 ? (
                    <TrendingUp className="w-3 h-3 inline mr-1" />
                  ) : (
                    <TrendingDown className="w-3 h-3 inline mr-1" />
                  )}
                  {isPresentationMode ? '~**%' : `${summary.sqls_change >= 0 ? '+' : ''}${summary.sqls_change || 0}%`} vs prior period
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card" data-testid="kpi-pipeline">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Pipeline Value</p>
                <p className="kpi-value">{isPresentationMode ? '~$**M' : `$${((summary.pipeline_value || 0) / 1000000).toFixed(1)}M`}</p>
                <p className={`kpi-change ${summary.pipeline_change >= 0 ? 'positive' : 'negative'}`}>
                  {summary.pipeline_change >= 0 ? (
                    <TrendingUp className="w-3 h-3 inline mr-1" />
                  ) : (
                    <TrendingDown className="w-3 h-3 inline mr-1" />
                  )}
                  {isPresentationMode ? '~**%' : `${summary.pipeline_change >= 0 ? '+' : ''}${summary.pipeline_change || 0}%`} vs prior period
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="charts-grid">
        {/* Traffic Chart - shows contact activity when HubSpot is not connected */}
        <Card className="chart-container" data-testid="traffic-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#00A5A8]" />
              Last 7 Days Contact Activity
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Solid lines: Current period • Dashed lines: Prior week
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={displayTrafficData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} tickFormatter={maskAxisTick} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                  }}
                  formatter={maskTooltip}
                />
                <Legend />
                {/* Current period - solid lines */}
                <Line
                  type="monotone"
                  dataKey="sessions"
                  name="New Contacts"
                  stroke={chartColors[0]}
                  strokeWidth={2}
                  dot={{ fill: chartColors[0] }}
                />
                <Line
                  type="monotone"
                  dataKey="users"
                  name="MQLs"
                  stroke={chartColors[1]}
                  strokeWidth={2}
                  dot={{ fill: chartColors[1] }}
                />
                {/* Prior period - dashed lines */}
                <Line
                  type="monotone"
                  dataKey="priorSessions"
                  name="Prior Contacts"
                  stroke="#94a3b8"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="priorUsers"
                  name="Prior MQLs"
                  stroke="#cbd5e1"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Lead Funnel Chart */}
        <Card className="chart-container" data-testid="funnel-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-[#00A5A8]" />
              Lead Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={lifecycleData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) =>
                    isPresentationMode ? name : `${name} ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {lifecycleData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={chartColors[index % chartColors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={maskTooltip} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Deal Pipeline Chart */}
      <Card className="chart-container mb-6" data-testid="pipeline-chart">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#00A5A8]" />
            Deal Pipeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dealStageData.length > 0 ? dealStageData : [
              { name: "Discovery", count: 8, value: 120000 },
              { name: "Qualified", count: 5, value: 85000 },
              { name: "Proposal", count: 3, value: 95000 },
              { name: "Negotiation", count: 2, value: 150000 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
              <YAxis yAxisId="left" stroke="#64748B" fontSize={12} tickFormatter={maskAxisTick} />
              <YAxis yAxisId="right" orientation="right" stroke="#64748B" fontSize={12} tickFormatter={maskAxisTick} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                }}
                formatter={(value, name) =>
                  isPresentationMode ? ['•••', name] : (name === "Value ($)" ? [`$${value.toLocaleString()}`, name] : [value, name])
                }
              />
              <Legend />
              <Bar yAxisId="left" dataKey="count" fill={chartColors[0]} name="Deals" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="value" fill={chartColors[1]} name="Value ($)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
